import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { useMedia } from 'react-use'
import { useSelector } from 'react-redux'
import { useS4LContext } from '@s4l/react-context'
import { useBrowser } from '@s4l/utils/hooks/useBrowser'

import { Typography } from '@s4l/ds'
import { cn, type UtilityClassNames } from '@s4l/utility-css'
import Modal from 'client/components/Modal'
import { VARIANT_FULLSCREEN, VARIANT_ALONE } from 'client/components/Modal/constants'
import { breakpoint } from 'client/styles'
import VerifyEmailModal from 'src/effects/VerifyEmail'

import FormFields from './FormFields/index'
import InvoiceInfo from './InvoiceInfo/index'
import TermsSection from './TermsSection/index'

import { getEmail } from 'src/comps/Voucher/selectors'
import { getStatus as getStatusEmail } from 'src/effects/VerifyEmail/selectors'
import { type InvoiceFormData, type RequestModalProps } from './types'
import styles from './index.module.css'

const RequestModal: React.FC<RequestModalProps> = ({
  hash,
  isOpen,
  onClose,
  onError,
  onSubmit,
}) => {
  const intl = useIntl()
  const { domain } = useS4LContext()
  const { device } = useBrowser()
  const isWide = useMedia(breakpoint.tablet, device !== 'mobile')
  const email = useSelector(getEmail)
  const statusResponseEmail = useSelector(getStatusEmail(hash))
  const isEmailValidated = email && statusResponseEmail === 'STATUS_OK'

  const [formData, setFormData] = useState<InvoiceFormData>({
    name: '',
    address: '',
    vatNumber: '',
    email: '',
  })

  if (!isOpen) return null

  const handleChange = (value: string, fieldName: keyof InvoiceFormData) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value,
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const [error] = await domain.CreateInvoiceRequestBookingUseCase.execute({
      bookingHash: hash,
      name: formData.name,
      address: formData.address,
      vatNumber: formData.vatNumber,
      email: formData.email,
    })

    if (error) {
      console.error(error) // eslint-disable-line no-console
      onError?.()
    } else {
      onSubmit?.()
    }
  }

  return (
    <Modal
      show={isOpen}
      handleClose={onClose}
      variant={isWide ? VARIANT_ALONE : VARIANT_FULLSCREEN}
      title={
        isEmailValidated &&
        intl.formatMessage({
          id: 'global.request-invoice',
        })
      }
    >
      {isEmailValidated ? (
        <>
          <Typography
            variant="body"
            size="l"
            as="p"
            className={cn(styles.formTitle) as UtilityClassNames}
          >
            {intl.formatMessage({
              id: 'global.fill-in-details',
            })}
          </Typography>

          <FormFields
            formData={formData}
            onSubmit={handleSubmit}
            onFieldChange={(value, fieldName) => handleChange(value, fieldName)}
          >
            <InvoiceInfo />
          </FormFields>

          <TermsSection />
        </>
      ) : (
        <VerifyEmailModal
          buttonText={intl.formatMessage({ id: 'global.request-invoice' })}
          hash={hash}
        />
      )}
    </Modal>
  )
}

export default RequestModal
